import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Empty, Input, InputNumber, Segmented, Skeleton, Table, Tooltip, message } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleFilled,
  SaveOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { StockRow, StockSheet, normaliseStockSheet, siteDailyApis } from "../../../../api/SiteDaily";
import { useFontScale } from "../../../../theme/fontScale";
import { fmtQty } from "../executionNorm";
import { EMPTY_SHEET_META, SheetSectionHandle, SheetSectionMeta, fromInput, inputValue } from "./daySheet";
import {
  StockCategoryGroup,
  StockDraft,
  draftOf,
  dayTotals,
  figuresOf,
  groupByCategory,
  hasMovement,
  matchesSearch,
  num,
  sameDraft,
  saveRowOf,
  varianceOf,
} from "./stockSheet";

// Store-Stock: what the project store held, received, issued and still holds —
// material by material, grouped by category, for ONE day.
//
// Unlike the other day sheets this one is PROJECT-scoped: a store holds material
// for the whole project, so there is no task tree, no tracking scope and no
// Civil pre-condition. The day is one flat sheet of the store's materials.
//
// The clerk fills Purchased (qty + rate), Issued, and an Adjustment for damage,
// returns and transfers. Opening is carried from the last day that had a row —
// editable only on a material's first ever day — and Closing is computed, so the
// value side always ties:
//   closing = opening + purchased + adjustment - issued, valued at the day's
//   weighted average cost.
//
// The Issued quantity is the STORE's own figure. What the tasks booked in
// Material-Stores is shown beside it as a reconciliation, with a colour and an
// icon when the two disagree — never a block, because the two ledgers are kept
// by different people and a day's difference is information, not a defect.

const MUTED = "#8094ae";
const INK = "#364a63";
const READ_ONLY = "#526484";
const GOOD = "#2b9a66";
const WARN = "#d9a545";
const BAD = "#cf1322";

const money = (value: number | null, strong = false) => (
  <span
    className="exec-num"
    style={{ color: value === null ? MUTED : INK, fontWeight: value === null || !strong ? 400 : 600 }}
  >
    {value === null ? "—" : `₹ ${fmtQty(value)}`}
  </span>
);

/** The store's issued quantity against what the tasks booked. */
const Variance: React.FC<{ row: StockRow; draft?: StockDraft }> = ({ row, draft }) => {
  const { state, diff } = varianceOf(row, draft);
  if (state === "UNIT_MISMATCH") {
    return (
      <Tooltip title={`The tasks booked this material in another unit — a quantity in one unit cannot be subtracted from a balance in ${row.uomName || "another"}, so there is no variance to show.`}>
        <span className="exec-num" style={{ color: MUTED }}>
          <WarningOutlined /> unit
        </span>
      </Tooltip>
    );
  }
  if (state === "NO_TASK_ISSUE" || diff === null) {
    return (
      <Tooltip title="No task booked this material on this date.">
        <span className="exec-num" style={{ color: MUTED }}>
          —
        </span>
      </Tooltip>
    );
  }
  if (state === "MATCH") {
    return (
      <Tooltip title="The store's issue matches what the tasks booked.">
        <span className="exec-num" style={{ color: GOOD }}>
          <CheckCircleFilled /> matches
        </span>
      </Tooltip>
    );
  }
  const high = state === "STORE_HIGHER";
  return (
    <Tooltip
      title={
        high
          ? `The store issued ${fmtQty(Math.abs(diff))} ${row.uomName} more than the tasks booked — material may have left the store without being booked to a task yet.`
          : `The tasks booked ${fmtQty(Math.abs(diff))} ${row.uomName} more than the store issued — the store's issue may be unrecorded.`
      }
    >
      <span className="exec-num" style={{ color: high ? WARN : BAD, fontWeight: 600 }}>
        {high ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {diff > 0 ? "+" : ""}
        {fmtQty(diff)}
      </span>
    </Tooltip>
  );
};

interface Props {
  projectId: number;
  date: string; // 'YYYY-MM-DD'
  /** Owned by the page: the box sits in the tab's single strip, beside the date. */
  search: string;
  reloadToken: number;
  onSheetChange: (meta: SheetSectionMeta) => void;
  onDirtyChange: (count: number) => void;
}

const StoreStockSection = forwardRef<SheetSectionHandle, Props>(
  ({ projectId, date, search, reloadToken, onSheetChange, onDirtyChange }, ref) => {
    const { scale } = useFontScale();
    const w = (base: number) => Math.round(base * scale);

    const [sheet, setSheet] = useState<StockSheet | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [drafts, setDrafts] = useState<Record<number, StockDraft>>({});
    const [initial, setInitial] = useState<Record<number, StockDraft>>({});
    // The value block is wide; a clerk on a narrow screen can drop it and keep
    // the quantities. Local state on purpose — not a URL concern.
    const [view, setView] = useState<"value" | "qty">("value");

    // Only the newest request may land: a slow earlier day must not overwrite the
    // day the user has since moved to.
    const requestRef = useRef(0);
    const draftsRef = useRef(drafts);
    draftsRef.current = drafts;

    const load = useCallback(() => {
      if (!projectId || !date) return;
      const requestId = ++requestRef.current;
      setLoading(true);
      siteDailyApis
        .stockSheet(projectId, { date })
        .then((res) => {
          if (requestId !== requestRef.current) return;
          if (res?.status !== true) {
            // Never leave the previous day's balances fused to a new date.
            setSheet(null);
            setDrafts({});
            setInitial({});
            setError(res?.message || "Could not load the store's stock");
            return;
          }
          setError("");
          const next = normaliseStockSheet(res.data);
          const seeded: Record<number, StockDraft> = {};
          next.rows.forEach((row) => {
            seeded[row.materialId] = draftOf(row);
          });
          setSheet(next);
          setInitial(seeded);
          setDrafts({ ...seeded });
        })
        .catch(() => {
          if (requestId !== requestRef.current) return;
          setSheet(null);
          setError("Could not load the store's stock");
        })
        .finally(() => {
          if (requestId === requestRef.current) setLoading(false);
        });
    }, [projectId, date]);

    useEffect(() => {
      load();
    }, [load, reloadToken]);

    const locked = !!sheet?.dayLocked;
    const disabled = locked || saving;

    // The page's StatCards and toolbar read this. The task-shaped fields do not
    // apply to a store — there is no task and no tracking here — so they stay at
    // their empty values rather than inventing a count.
    useEffect(() => {
      onSheetChange({
        ...EMPTY_SHEET_META,
        loading,
        hasSheet: !!sheet,
        taskCount: sheet?.materialCount ?? 0,
        recordedTaskCount: sheet?.movementCount ?? 0,
        dayLocked: locked,
      });
    }, [sheet, loading, locked, onSheetChange]);

    const dirtyIds = useMemo(
      () =>
        Object.keys(drafts)
          .map(Number)
          .filter((materialId) => !sameDraft(drafts[materialId], initial[materialId])),
      [drafts, initial]
    );
    useEffect(() => {
      onDirtyChange(dirtyIds.length);
    }, [dirtyIds, onDirtyChange]);

    const setDraft = (materialId: number, patch: Partial<StockDraft>) => {
      setDrafts((current) => ({
        ...current,
        [materialId]: { ...current[materialId], ...patch },
      }));
    };

    const figuresFor = useCallback(
      (row: StockRow) => figuresOf(row, draftsRef.current[row.materialId]),
      // Recomputed on every keystroke so the closing balance moves as the clerk
      // types; the sheet is ~70 rows, which is nothing to re-derive.
      [drafts] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const movedFor = useCallback(
      (row: StockRow) => hasMovement(draftsRef.current[row.materialId]),
      [drafts] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Filtering is PRESENTATION ONLY. `save` below builds its replace-set from
    // sheet.rows, not from this — a save of the visible rows alone would delete
    // every material the search had hidden.
    const visibleRows = useMemo(
      () => (sheet ? sheet.rows.filter((row) => matchesSearch(row, search)) : []),
      [sheet, search]
    );
    const groups = useMemo<StockCategoryGroup[]>(
      () => groupByCategory(visibleRows, figuresFor, movedFor),
      [visibleRows, figuresFor, movedFor]
    );
    // Totals describe what is ON SCREEN: a subtotal that does not add up to the
    // rows beneath it is worse than no subtotal, so a search narrows them too and
    // the header says how many materials they cover.
    const totals = useMemo(() => dayTotals(groups), [groups]);
    const searching = search.trim() !== "";
    // Changed rows the search is hiding — Reset and Save act on the whole day, so
    // a clerk who filters after typing has to be told what else is in flight.
    const hiddenDirty = useMemo(
      () =>
        searching
          ? dirtyIds.filter(
              (materialId) => !visibleRows.some((row) => row.materialId === materialId)
            ).length
          : 0,
      [searching, dirtyIds, visibleRows]
    );

    const save = useCallback(async (): Promise<boolean> => {
      if (!sheet) return false;
      // A FULL replace-set for the day: every material that still has movement
      // goes, not only the ones just edited — sending the dirty rows alone would
      // delete everything else recorded for the date. Read from sheet.rows, NEVER
      // from the search-filtered view, for the same reason: a day saved while a
      // filter was on would wipe every material the filter hid.
      const rows = sheet.rows
        .filter((row) => hasMovement(draftsRef.current[row.materialId]))
        .map((row) => saveRowOf(row, draftsRef.current[row.materialId]));
      setSaving(true);
      try {
        const res = await siteDailyApis.saveStock({ projectId, stockDate: date, rows });
        if (res?.status !== true) {
          message.error(res?.message || "Could not save the store's stock");
          return false;
        }
        if (res?.data?.warning) message.warning(res.data.warning, 6);
        else message.success(res?.message || "Store stock saved");
        load();
        return true;
      } catch {
        message.error("Could not save the store's stock");
        return false;
      } finally {
        setSaving(false);
      }
    }, [sheet, projectId, date, load]);

    useImperativeHandle(ref, () => ({
      saveAll: async () => {
        if (!dirtyIds.length) return;
        await save();
      },
      discardAll: () => setDrafts({ ...initial }),
    }));

    const widths = {
      material: w(230),
      oQty: w(110),
      oRate: w(100),
      oValue: w(110),
      pQty: w(120),
      pRate: w(120),
      pValue: w(110),
      iQty: w(120),
      iRate: w(120),
      iValue: w(110),
      variance: w(130),
      aQty: w(120),
      aReason: w(150),
      cQty: w(110),
      cRate: w(100),
      cValue: w(120),
      remarks: w(170),
    };
    const hidden: Array<keyof typeof widths> = view === "value" ? [] : ["oValue", "pValue", "iValue", "cRate", "cValue"];
    const scrollX = (Object.keys(widths) as Array<keyof typeof widths>)
      .filter((key) => !hidden.includes(key))
      .reduce((acc, key) => acc + widths[key], 0);

    const qtyInput = (
      row: StockRow,
      field: keyof StockDraft,
      options: { min?: string; placeholder?: string } = {}
    ) => (
      <InputNumber
        stringMode
        min={options.min ?? "0"}
        step="0.01"
        placeholder={options.placeholder ?? "0.00"}
        value={inputValue(drafts[row.materialId]?.[field] ?? "")}
        onChange={(v) => setDraft(row.materialId, { [field]: fromInput(v) } as Partial<StockDraft>)}
        disabled={disabled}
      />
    );

    const materialColumn = {
      title: "Material",
      key: "material",
      width: widths.material,
      fixed: "left" as const,
      render: (_: any, row: StockRow) => (
        <div>
          <div style={{ color: "#022c57", fontWeight: 600 }}>{row.materialName}</div>
          <div className="pms-exec-hint">
            {row.uomName || "no unit"}
            {row.productCode ? ` · ${row.productCode}` : ""}
          </div>
        </div>
      ),
    };

    const openingColumns = {
      title: "Opening",
      key: "opening",
      children: [
        {
          title: "Qty",
          key: "oQty",
          width: widths.oQty,
          align: "right" as const,
          render: (_: any, row: StockRow) => {
            // Carried in: read-only. The server rejects an opening that
            // contradicts the carry, because the one promise this ledger makes is
            // that each day starts where the last one ended. Corrections go
            // through the Adjustment column.
            if (row.carriedFrom !== "") {
              return (
                <Tooltip title={`Carried from ${dayjs(row.carriedFrom).format("DD-MMM-YYYY")}. To correct it, use Adjustment.`}>
                  <span className="exec-num" style={{ color: READ_ONLY }}>
                    {fmtQty(figuresFor(row).openingQty)}
                  </span>
                </Tooltip>
              );
            }
            return (
              <div>
                {qtyInput(row, "openingQty")}
                <div className="pms-exec-hint">first day — set it</div>
              </div>
            );
          },
        },
        {
          title: "Rate",
          key: "oRate",
          width: widths.oRate,
          align: "right" as const,
          render: (_: any, row: StockRow) => {
            if (row.carriedFrom !== "") {
              const figures = figuresFor(row);
              const rate = figures.openingQty > 0 && figures.openingValue > 0 ? figures.openingValue / figures.openingQty : null;
              return (
                <span className="exec-num" style={{ color: rate === null ? MUTED : READ_ONLY }}>
                  {rate === null ? "—" : fmtQty(rate)}
                </span>
              );
            }
            return qtyInput(row, "openingRate");
          },
        },
        {
          title: "Value",
          key: "oValue",
          width: widths.oValue,
          align: "right" as const,
          render: (_: any, row: StockRow) => money(figuresFor(row).openingValue),
        },
      ],
    };

    const purchasedColumns = {
      title: "Purchased / received",
      key: "purchased",
      children: [
        {
          title: "Qty",
          key: "pQty",
          width: widths.pQty,
          align: "right" as const,
          render: (_: any, row: StockRow) => qtyInput(row, "purchasedQty"),
        },
        {
          title: "Rate",
          key: "pRate",
          width: widths.pRate,
          align: "right" as const,
          render: (_: any, row: StockRow) => {
            const draft = drafts[row.materialId];
            const needsRate = num(draft?.purchasedQty ?? "") > 0 && (draft?.purchasedRate ?? "") === "";
            // The norm-price comparison is deliberately NOT here: entering a
            // receipt is a data-entry job, and what the project assumed the
            // material would cost is a costing question for the day-wise review,
            // where it is flagged on the rate itself.
            return (
              <div>
                {qtyInput(row, "purchasedRate")}
                {needsRate && <div className="pms-exec-hint pms-exec-hint--amber">rate required</div>}
              </div>
            );
          },
        },
        {
          title: "Value",
          key: "pValue",
          width: widths.pValue,
          align: "right" as const,
          render: (_: any, row: StockRow) => money(figuresFor(row).purchasedValue),
        },
      ],
    };

    const issuedColumns = {
      title: "Issued",
      key: "issued",
      children: [
        {
          title: "Qty",
          key: "iQty",
          width: widths.iQty,
          align: "right" as const,
          // Neither norm-derived check is shown here: what the norms allocated
          // and what they assumed the material costs are questions for the
          // day-wise review, where they are flagged on the figure itself. This
          // screen is for recording what happened.
          render: (_: any, row: StockRow) => qtyInput(row, "issuedQty"),
        },
        {
          title: "Rate",
          key: "iRate",
          width: widths.iRate,
          align: "right" as const,
          render: (_: any, row: StockRow) => qtyInput(row, "issuedRate"),
        },
        {
          title: "Value",
          key: "iValue",
          width: widths.iValue,
          align: "right" as const,
          render: (_: any, row: StockRow) => {
            const figures = figuresFor(row);
            if (figures.avgRate === null && figures.issuedQty > 0) {
              return (
                <Tooltip title="Nothing priced to value this issue — set an opening rate or record the receipt.">
                  <span className="exec-num" style={{ color: WARN }}>
                    unpriced
                  </span>
                </Tooltip>
              );
            }
            return money(figures.issuedValue, true);
          },
        },
        {
          title: "vs tasks",
          key: "variance",
          width: widths.variance,
          align: "right" as const,
          render: (_: any, row: StockRow) => (
            <div>
              <Variance row={row} draft={drafts[row.materialId]} />
              {row.taskIssuedQty !== "" && (
                <div className="pms-exec-hint">
                  booked <span className="exec-num">{fmtQty(row.taskIssuedQty)}</span>
                </div>
              )}
            </div>
          ),
        },
      ],
    };

    const adjustmentColumns = {
      title: "Adjustment",
      key: "adjustment",
      children: [
        {
          title: "Qty (+/-)",
          key: "aQty",
          width: widths.aQty,
          align: "right" as const,
          // The only signed input on the sheet: damage and transfers out are
          // negative, returns to the store positive.
          render: (_: any, row: StockRow) => qtyInput(row, "adjustmentQty", { min: "-999999999", placeholder: "0.00" }),
        },
        {
          title: "Reason",
          key: "aReason",
          width: widths.aReason,
          render: (_: any, row: StockRow) => {
            const draft = drafts[row.materialId];
            const needsReason = Math.abs(num(draft?.adjustmentQty ?? "")) > 0 && !draft?.adjustmentReason;
            return (
              <div>
                <Input
                  size="small"
                  value={draft?.adjustmentReason ?? ""}
                  onChange={(e) => setDraft(row.materialId, { adjustmentReason: e.target.value })}
                  maxLength={120}
                  placeholder="damage, return…"
                  disabled={disabled}
                />
                {needsReason && <div className="pms-exec-hint pms-exec-hint--amber">say why</div>}
              </div>
            );
          },
        },
      ],
    };

    const closingColumns = {
      title: "Closing",
      key: "closing",
      children: [
        {
          title: "Qty",
          key: "cQty",
          width: widths.cQty,
          align: "right" as const,
          render: (_: any, row: StockRow) => {
            const figures = figuresFor(row);
            const negative = figures.closingQty < 0;
            return (
              <Tooltip
                title={
                  negative
                    ? "More has gone out than came in — a receipt is probably not recorded yet."
                    : "Opening + purchased + adjustment − issued."
                }
              >
                <span
                  className="exec-num"
                  style={{ color: negative ? BAD : INK, fontWeight: 600 }}
                >
                  {negative && <WarningOutlined />} {fmtQty(figures.closingQty)}
                </span>
              </Tooltip>
            );
          },
        },
        {
          title: "Avg rate",
          key: "cRate",
          width: widths.cRate,
          align: "right" as const,
          render: (_: any, row: StockRow) => {
            const { avgRate } = figuresFor(row);
            return (
              <Tooltip title="The day's weighted average cost — (opening value + purchase value) ÷ (opening qty + purchase qty). Issues and the closing balance are valued at it.">
                <span className="exec-num" style={{ color: avgRate === null ? MUTED : READ_ONLY }}>
                  {avgRate === null ? "—" : fmtQty(avgRate)}
                </span>
              </Tooltip>
            );
          },
        },
        {
          title: "Value",
          key: "cValue",
          width: widths.cValue,
          align: "right" as const,
          render: (_: any, row: StockRow) => money(figuresFor(row).closingValue, true),
        },
      ],
    };

    const remarksColumn = {
      title: "Remarks",
      key: "remarks",
      width: widths.remarks,
      render: (_: any, row: StockRow) => (
        <Input
          size="small"
          value={drafts[row.materialId]?.remarks ?? ""}
          onChange={(e) => setDraft(row.materialId, { remarks: e.target.value })}
          maxLength={200}
          placeholder="—"
          disabled={disabled}
        />
      ),
    };

    const strip = (column: any) =>
      column.children
        ? { ...column, children: column.children.filter((child: any) => !hidden.includes(child.key)) }
        : column;
    const columns = [
      materialColumn,
      openingColumns,
      purchasedColumns,
      issuedColumns,
      adjustmentColumns,
      closingColumns,
      remarksColumn,
    ].map(strip);

    const figure = (label: string, value: number) => (
      <span className="pms-exec-heads-figure">
        <span className="pms-exec-heads-subtotal">
          {label} ₹ {fmtQty(value)}
        </span>
      </span>
    );

    if (loading && !sheet) return <Skeleton active title={false} paragraph={{ rows: 6 }} />;
    if (error && !sheet) {
      return (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button size="small" onClick={load}>
              Retry
            </Button>
          }
        />
      );
    }
    if (!sheet || !sheet.rows.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              No materials in this project's store yet
              <br />
              <span style={{ color: MUTED, fontSize: 12 }}>
                The sheet lists every material the project plans to consume, plus anything already carrying
                a balance — apply material norms to the tasks first.
              </span>
            </span>
          }
        />
      );
    }

    return (
      <>
        {locked && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Day locked — the DPR for ${dayjs(date).format("DD-MMM-YYYY")} has been submitted`}
            description="The store's stock for this date is read-only."
          />
        )}
        <div className="pms-exec-lines">
          <div className="pms-exec-lines-head">
            <span>
              <div className="pms-exec-hint">
                {searching ? (
                  <>
                    showing {totals.materialCount} of {sheet.materialCount} material
                    {sheet.materialCount === 1 ? "" : "s"}
                    {groups.length > 0 && (
                      <>
                        {" in "}
                        {groups.length} categor{groups.length === 1 ? "y" : "ies"}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {totals.materialCount} material{totals.materialCount === 1 ? "" : "s"} in {groups.length} categor
                    {groups.length === 1 ? "y" : "ies"} · {totals.movementCount} moved today
                  </>
                )}
                {sheet.mismatchCount > 0 && (
                  <>
                    {" · "}
                    <span className="pms-exec-hint--amber">
                      {sheet.mismatchCount} differ{sheet.mismatchCount === 1 ? "s" : ""} from the task issues
                    </span>
                  </>
                )}
              </div>
            </span>
            <span className="pms-exec-cat-figures">
              {figure("opening", totals.openingValue)}
              {figure("purchased", totals.purchasedValue)}
              {figure("issued", totals.issuedValue)}
              {figure("closing", totals.closingValue)}
            </span>
          </div>

          {/* The search box has to stay reachable, so a no-match is rendered
              INSIDE the sheet rather than replacing it. */}
          {searching && !groups.length && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  No material, code or category matches “{search.trim()}”
                  <br />
                  <span style={{ color: MUTED, fontSize: 12 }}>Clear the search box above to see the store again.</span>
                </span>
              }
            />
          )}

          {groups.map((group) => (
            <div className="pms-exec-heads-block" key={group.key} style={{ marginBottom: 12 }}>
              <div className="pms-exec-lines-head" style={{ marginBottom: 6 }}>
                <span>
                  <span className="pms-eyebrow">{group.categoryName}</span>
                  <div className="pms-exec-hint">
                    {group.materialCount} material{group.materialCount === 1 ? "" : "s"} · {group.movementCount} moved
                    {group.categoryId === 0 && " · no category on the material master"}
                  </div>
                </span>
                <span className="pms-exec-cat-figures">
                  {figure("opening", group.openingValue)}
                  {figure("closing", group.closingValue)}
                </span>
              </div>
              <Table
                size="small"
                className="pms-exec-heads-table pms-stock-table"
                rowKey={(row: StockRow) => row.materialId}
                dataSource={group.rows}
                columns={columns as any}
                pagination={false}
                scroll={{ x: scrollX }}
              />
            </div>
          ))}

          <div className="pms-exec-heads-total">
            <span>
              <span className="pms-eyebrow">Store stock today</span>
              <div className="pms-exec-hint">
                Closing = opening + purchased + adjustment − issued. Issues and adjustments are valued at the
                day's weighted average cost, so the value always ties. Today's closing is the next day's
                opening.
              </div>
              {searching && (
                <div className="pms-exec-hint pms-exec-hint--amber">
                  Filtered — these figures cover the {totals.materialCount} material
                  {totals.materialCount === 1 ? "" : "s"} shown, not the whole store.
                </div>
              )}
            </span>
            <span className="pms-exec-cat-figures">
              {figure("opening", totals.openingValue)}
              {figure("purchased", totals.purchasedValue)}
              {figure("issued", totals.issuedValue)}
              {figure("adjustment", totals.adjustmentValue)}
              {figure("closing", totals.closingValue)}
            </span>
          </div>

          <div className="pms-exec-lines-foot">
            <span className="pms-exec-hint">
              Blank = nothing moved. A material left untouched keeps carrying its balance.
              {hiddenDirty > 0 && (
                <>
                  {" "}
                  <span className="pms-exec-hint--amber">
                    Save and Reset cover the whole day — {hiddenDirty} changed material
                    {hiddenDirty === 1 ? "" : "s"} {hiddenDirty === 1 ? "is" : "are"} hidden by the search.
                  </span>
                </>
              )}
            </span>
            <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
              <Segmented
                size="small"
                value={view}
                onChange={(v) => setView(v as "value" | "qty")}
                options={[
                  { label: "Qty + value", value: "value" },
                  { label: "Qty only", value: "qty" },
                ]}
              />
              {!locked && (
                <>
                  <Button
                    size="small"
                    onClick={() => setDrafts({ ...initial })}
                    disabled={!dirtyIds.length || saving}
                  >
                    Reset
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={save}
                    disabled={!dirtyIds.length}
                    loading={saving}
                  >
                    Save {dayjs(date).format("DD-MMM")}
                  </Button>
                </>
              )}
            </span>
          </div>
        </div>
      </>
    );
  }
);

export default StoreStockSection;
